<script setup lang="ts">
/**
 * The stage (port of stage.js): the piece always centre stage, plus per-step overlays —
 * reference photo and the fitted elevation (Check), shelf handles (Shape), Inspect (Look:
 * the live model pulled apart, part picking), the view bar (Viewing rN / Inspecting), the part
 * card and the framing inset (K7) that keeps overlays off the model.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { fmtMm, mm, setAppearance } from '@/domain';
import { mountBookcase, type BookcaseViewer, type ViewName } from '@/viewer';
import Icon from './Icon.vue';
import StageRef from './StageRef.vue';
import ShelfHandles from './ShelfHandles.vue';
import { app, shownBuild, snap, stagePreview, ui, viewerRef } from '@/app/state';
import { announce } from '@/app/announce';
import { go, hover, pickOpening, select } from '@/app/navigation';
import { grainArrow, grainText, lineLabel, plural, prefersReducedMotion } from '@/app/format';
import { fitOverlay } from '@/app/overlay';
import { checkMaterial } from '@/app/fit-extras';
import { MATERIAL_CHOICES } from '@/families/stock';
import { projectHasValues } from '@/app/analysis';
import type { BuildModel } from '@/domain';

const ACCENT = { light: '#0756bf', dark: '#7fb0ff' } as const; // --sel, Semblaro action blue
const STEP_DEFAULTS: Record<string, { view: string; dims: boolean }> = {
  reference: { view: 'three-quarter', dims: false },
  check: { view: 'front', dims: false },
  shape: { view: 'front', dims: true },
  look: { view: 'three-quarter', dims: false },
  pack: { view: 'three-quarter', dims: false },
};
const VIEWS: [string, string][] = [['front', 'Front'], ['three-quarter', '¾'], ['side', 'Side'], ['edge-detail', 'Edge']];

const stageEl = ref<HTMLElement | null>(null);
const modelEl = ref<HTMLElement | null>(null);
const host = ref<HTMLElement | null>(null);
const toolsEl = ref<HTMLElement | null>(null);
const barEl = ref<HTMLElement | null>(null);
const footEl = ref<HTMLElement | null>(null);
const linesEl = ref<SVGSVGElement | null>(null);
const hostTitle = ref('');
const dragging = ref(false);
let viewer: BookcaseViewer | null = null;
let shown: unknown = null;
let ro: ResizeObserver | null = null;
let offFrame: (() => void) | null = null;

const opts = ui.view;
const props = computed(() => snap.value.design.appearance?.props !== false);
const viewingRev = computed(() => (snap.value.viewing ? app.store.revision(snap.value.viewing) : null));
const mode = computed(() => ui.step);

/**
 * FND-18: after a refusal on a project with no values nothing was rebuilt, so the stage shows the
 * photo (or a neutral empty state), never a finished-looking default bookcase.
 */
const refusedStage = computed(() => ui.analysis.status === 'refused' && !projectHasValues() && (mode.value === 'reference' || mode.value === 'check'));
const refusalPhoto = computed(() => ui.ref.images[0] ?? null);

/**
 * FND-07: Check previews the reconstruction in the kit material closest to the photo's tone
 * (sheesham dark, oak mid, birch pale) when the fit read one; appearance only, nothing is cut from it.
 * The stage says which material it shows and that the material is chosen in Look.
 */
const speciesOf = (id: string) => (/sheesham/.test(id) ? 'sheesham' : /oak/.test(id) ? 'oak' : /birch|ply/.test(id) ? 'birch' : id);
const preview = computed(() => {
  if (mode.value !== 'check' || snap.value.viewing) return null;
  const m = checkMaterial.value;
  // appearance only: the model is shown in the photo's timber; a design already in that timber keeps its own board
  return m && speciesOf(m.stockId) !== speciesOf(snap.value.design.stockProfileId) ? m : null;
});
const stageBuild = computed<BuildModel>(() => {
  const b = shownBuild.value;
  const m = preview.value;
  if (!m) return b;
  return { ...b, parts: b.parts.map((p) => (p.role === 'back' ? p : { ...p, stockId: m.stockId, material: m.label })) };
});
const materialNote = computed(() => {
  if (mode.value !== 'check' || refusedStage.value) return null;
  const m = checkMaterial.value;
  const id = preview.value?.stockId ?? snap.value.design.stockProfileId;
  const label = (preview.value?.label ?? MATERIAL_CHOICES.find((c) => c.stockId === id || c.thicker?.some((t) => t.stockId === id))?.label ?? snap.value.stockInfo.material).toLowerCase();
  if (!m) return `Shown in ${label} as a placeholder · choose the material in Look`;
  if (m.tone === 'painted') return `Shown in ${label} · the photo shows a painted, white or black finish; paint isn’t modelled yet · choose the material in Look`;
  // three timbers only: say it is the nearest, never that it matches (stress-v2 F2: "matched" beside a visibly different wood)
  return `Shown in ${label}, the nearest of our three timbers to the photo’s tone · choose the material in Look`;
});

// ---------------------------------------------------------------------------------------------
// Viewer lifecycle
// ---------------------------------------------------------------------------------------------
onMounted(() => {
  const theme = ui.effectiveTheme;
  opts.figure = window.innerWidth > 700;
  viewer = mountBookcase(host.value!, {
    theme, accent: ACCENT[theme], props: props.value, scaleFigure: opts.figure, view: 'three-quarter',
    onPick: (id: string | null) => select(id, { from: 'viewer' }),
    onHover: (id: string | null) => hover(id, { from: 'viewer' }),
    onOpeningPick: (i: number, additive: boolean) => pickOpening(i, additive),
    onOpeningHover: (i: number | null) => { hostTitle.value = i == null || snap.value.viewing || ui.inspect || ui.explode ? '' : `${snap.value.build.openings[i]?.label ?? `Opening ${i + 1}`}: ${ui.frontPicking && ui.step === 'shape' ? 'click to pick for fronts; Shift-click to pick several' : 'click to edit its height'}`; },
  });
  viewerRef.value = viewer;
  // Apply the saved finish and fixings before the first frame, not only after the first edit.
  viewer.setOptions(appearanceOpts(shownBuild.value.design));
  viewer.update(stageBuild.value, { animate: false });
  shown = stageBuild.value;
  applyMode(ui.step, true);
  offFrame = viewer.onFrame(() => drawModelLines());
  ro = new ResizeObserver(() => requestAnimationFrame(updateInset));
  ro.observe(stageEl.value!);
});
onBeforeUnmount(() => {
  offFrame?.();
  ro?.disconnect();
  viewer?.dispose();
  viewerRef.value = null;
});

watch(stageBuild, (b) => {
  if (!viewer || stagePreview.value) return;
  if (b === shown) return;
  viewer.setOptions({ props: b.design.appearance?.props !== false, ...appearanceOpts(b.design) });
  viewer.update(b);
  shown = b;
});
/** Look's choices for the kit's material loader (a hook; see ViewerOptions.appearance). */
function appearanceOpts(d: typeof snap.value.design) {
  return {
    backdrop: d.appearance?.staging === 'room',
    appearance: { materialId: snap.value.stockInfo.material, stockId: d.stockProfileId, finishId: d.appearance?.finish ?? null, showHardware: !!d.appearance?.hardware },
  };
}
watch(() => ui.effectiveTheme, (t) => { viewer?.setTheme(t); viewer?.setOptions({ accent: ACCENT[t] }); });

// ---------------------------------------------------------------------------------------------
// Mode (step defaults)
// ---------------------------------------------------------------------------------------------
function applyMode(step: string, first = false) {
  if (!viewer) return;
  const d = STEP_DEFAULTS[step] ?? STEP_DEFAULTS.reference!;
  opts.dims = d.dims;
  opts.view = d.view;
  viewer.setOptions({ showDims: opts.dims, scaleFigure: step === 'check' ? false : opts.figure, exploded: step === 'look' && ui.explode, detail: step === 'look' && ui.inspect });
  if (step !== 'shape') viewer.highlightOpening(null);
  requestAnimationFrame(() => requestAnimationFrame(() => {
    viewer?.resize();
    updateInset();
    viewer?.setView(opts.view as ViewName, { animate: !first && !prefersReducedMotion() });
  }));
  setTimeout(() => { viewer?.resize(); updateInset(); }, 620);
}
watch(() => ui.step, (s, p) => { if (s !== p) applyMode(s); });

// ---------------------------------------------------------------------------------------------
// Framing inset: overlays never clip the model or its dimension chains (K7)
// ---------------------------------------------------------------------------------------------
function updateInset() {
  if (!viewer || !modelEl.value) return;
  const sr = modelEl.value.getBoundingClientRect();
  if (!sr.width) return;
  const compact = window.innerWidth <= 640;
  let top = 12, bottom = 12, right = 0, left = 0;
  const tools = toolsEl.value;
  if (tools && getComputedStyle(tools).display !== 'none' && tools.offsetHeight) top = Math.max(top, tools.getBoundingClientRect().bottom - sr.top + 8);
  if (barEl.value) top = Math.max(top, barEl.value.getBoundingClientRect().bottom - sr.top + 8);
  const ms = footEl.value?.querySelector('.edit-ms');
  if (footEl.value && ms && getComputedStyle(ms).display !== 'none' && ms.textContent) bottom = Math.max(bottom, sr.bottom - footEl.value.getBoundingClientRect().top + 4);
  if (opts.dims) bottom = Math.max(bottom, 30);
  if (mode.value === 'shape' && !compact && !snap.value.viewing) right = 150;
  if (mode.value === 'shape' && opts.dims && !compact) left = 24;
  // Check: the model's shelf tags sit right of the lines; keep room so they never clip (ui-spec §5 item 2)
  if (mode.value === 'check' && showModelLines.value) right = compact ? 72 : 64;
  viewer.setOptions({ inset: { top: Math.round(top), bottom: Math.round(bottom), left, right } });
}

// ---------------------------------------------------------------------------------------------
// Tools
// ---------------------------------------------------------------------------------------------
function setView(v: string, label: string) {
  opts.view = v;
  viewer?.setView(v as ViewName);
  announce(`${label} view.`);
}
function toggleFigure() { opts.figure = !opts.figure; viewer?.setOptions({ scaleFigure: opts.figure }); }
function toggleDims() { opts.dims = !opts.dims; viewer?.setOptions({ showDims: opts.dims }); void nextTick(updateInset); }
function toggleBooks() {
  const on = snap.value.design.appearance?.props === false;
  app.store.apply((d) => setAppearance(d, { props: on }), { label: on ? 'Books shown' : 'Books hidden' });
}

// ---------------------------------------------------------------------------------------------
// View bar (Viewing rN / Inspecting the live model)
// ---------------------------------------------------------------------------------------------
const viewBar = computed(() => {
  const rev = viewingRev.value;
  if (rev) {
    // D10: a revision the generator now builds differently says so; it needs a new revision before it is exported again
    const regenerated = app.store.revisionIdentity(rev.id) === 'regenerated';
    return { kind: 'view' as const, title: `Viewing ${rev.id} · ${rev.label}`, sub: regenerated ? 'Not your current design. Rebuilt with a newer version of this style, so it may differ from when it was saved.' : 'Not your current design. Nothing has changed.' };
  }
  if ((ui.inspect || ui.explode) && mode.value === 'look') {
    const s = snap.value;
    const sub = ui.inspect && ui.explode ? 'Detailed pieces, pulled apart. Select one to find it in the cut list.' : ui.inspect ? 'Detailed pieces, assembled. Select one to find it in the cut list.' : 'Parts pulled apart. Nothing about the design changes.';
    return { kind: 'inspect' as const, title: `${ui.inspect ? 'Inspecting' : 'Exploded'} ${s.currentRevisionId ?? 'your design'}${s.isDirty && s.currentRevisionId ? ' + edits' : ''} · live model`, sub };
  }
  return null;
});
function endView() {
  const wasInspect = ui.inspect || ui.explode;
  ui.inspect = false;
  ui.explode = false;
  ui.sel = null;
  viewer?.setOptions({ exploded: false, detail: false, frontsOpen: false });
  app.store.backToLatest();
  announce(wasInspect ? 'Back to the assembled model.' : 'Back to the latest design.');
}

// ---------------------------------------------------------------------------------------------
// Check: matching shelf lines on the model
// ---------------------------------------------------------------------------------------------
const showModelLines = computed(() => mode.value === 'check' && !!fitOverlay.value && !snap.value.viewing);
function drawModelLines() {
  const svg = linesEl.value;
  if (!svg || !viewer) return;
  if (!showModelLines.value) { if (svg.childNodes.length) svg.replaceChildren(); return; }
  const b = shownBuild.value;
  const lit = new Set(ui.litLines);
  const out: string[] = [];
  for (const p of b.parts.filter((x) => x.role === 'top' || x.role === 'bottom' || (x.role === 'shelf' && (x.bay ?? 0) === 0))) {
    const y = p.placement.y + p.placement.sy / 2;
    const a = viewer.getScreenPosition({ point: { x: -30, y, z: b.envelope.D + 1 } });
    const c = viewer.getScreenPosition({ point: { x: b.envelope.W + 30, y, z: b.envelope.D + 1 } });
    if (!a || !c) continue;
    const on = lit.has(p.id) ? ' is-lit' : '';
    out.push(`<line class="model-line${on}" x1="${a.x.toFixed(1)}" y1="${a.y.toFixed(1)}" x2="${c.x.toFixed(1)}" y2="${c.y.toFixed(1)}"/>`);
    out.push(`<text class="model-tag${on}" x="${(c.x + 6).toFixed(1)}" y="${c.y.toFixed(1)}" dominant-baseline="central">${lineLabel(p.id)}</text>`);
  }
  const html = out.join('');
  if (svg.innerHTML !== html) svg.innerHTML = html;
}
watch(() => [ui.litLines.join(), showModelLines.value], () => drawModelLines());

// ---------------------------------------------------------------------------------------------
// Part card
// ---------------------------------------------------------------------------------------------
const selPart = computed(() => (ui.sel && mode.value !== 'check' ? shownBuild.value.parts.find((p) => p.id === ui.sel) ?? null : null));

// ---------------------------------------------------------------------------------------------
// Readouts
// ---------------------------------------------------------------------------------------------
const editMs = computed(() => (snap.value.canUndo ? `Updated in ${Math.max(0.1, snap.value.lastEditMs).toFixed(1)} ms` : ''));
const modelDesc = computed(() => {
  const b = shownBuild.value;
  const d = b.design;
  const sel = ui.sel ? b.parts.find((p) => p.id === ui.sel) : null;
  const what = (ui.inspect || ui.explode) && mode.value === 'look' ? `${ui.inspect ? 'Inspecting the live model' : 'The live model'}${ui.explode ? ', parts pulled apart' : ', assembled'}. ` : snap.value.viewing ? `Viewing ${snap.value.viewing}, not your current design. ` : '';
  const bays = b.bays.length;
  const layout = bays > 1
    ? `${bays} bays, ${b.bays.map((x) => plural(x.n, 'shelf', 'shelves')).join(', ')}; openings bay by bay from the bottom ${b.bays.map((x) => b.openings.filter((o) => (o.bay ?? 0) === x.index).map((o) => fmtMm(o.clear)).join(', ')).join('; ')} mm.`
    : `${plural(d.n, 'shelf', 'shelves')}; openings from the bottom ${b.openings.map((o) => fmtMm(o.clear)).join(', ')} mm.`;
  return `${what}${b.familyLabel} in ${snap.value.stockInfo.material.toLowerCase()}, ${fmtMm(d.W)} wide, ${fmtMm(d.H)} high, ${fmtMm(d.D)} deep, ${layout}${sel ? ` Selected: ${sel.label}, ${fmtMm(sel.finished.l)} by ${fmtMm(sel.finished.w)} by ${fmtMm(sel.finished.t)} mm.` : ''}`;
});
const datumText = (v: number) => fmtMm(mm(v));
const insetDeps = computed(() => [!!viewBar.value, ui.step, opts.dims].join());
watch(insetDeps, () => nextTick(() => requestAnimationFrame(updateInset)));
</script>

<template>
  <section
    id="stage"
    ref="stageEl"
    class="stage"
    aria-label="Bookcase preview"
    aria-describedby="modelDesc"
    :data-mode="mode"
    :class="{ 'is-viewing': !!viewBar, 'is-dragging': dragging, 'is-refused': refusedStage }"
  >
    <p id="modelDesc" class="sr-only">{{ modelDesc }}</p>
    <div v-if="refusedStage" class="stage-refusal" data-stage="refused">
      <figure v-if="refusalPhoto" class="refusal-photo">
        <img :src="refusalPhoto.src" :alt="refusalPhoto.alt">
        <figcaption>Your photo · nothing was rebuilt from it, so there is no model to show</figcaption>
      </figure>
      <p v-else class="refusal-empty">Nothing was rebuilt, so there is no model to show.</p>
    </div>
    <div class="stage-inner">
      <figure id="stageRef" class="stage-ref" aria-label="Reference photo with the fitted elevation"><StageRef v-if="mode === 'check'" /></figure>
      <div id="stageModel" ref="modelEl" class="stage-model">
        <div id="viewerHost" ref="host" class="viewer-host" :title="hostTitle" />
        <svg id="modelLines" ref="linesEl" class="model-lines" aria-hidden="true" />
        <ShelfHandles v-if="mode === 'shape' && !snap.viewing" :stage="stageEl" @dragging="dragging = $event" />
      </div>
    </div>

    <div id="stageTools" ref="toolsEl" class="stage-tools" role="toolbar" aria-label="View">
      <div class="seg" role="group" aria-label="Camera view">
        <button
          v-for="[v, l] in VIEWS"
          :key="v"
          type="button"
          :aria-pressed="opts.view === v"
          :data-view="v"
          :aria-label="v === 'three-quarter' ? 'Three-quarter' : undefined"
          @click="setView(v, v === 'three-quarter' ? 'Three-quarter' : l)"
        >
          {{ l }}
        </button>
      </div>
      <div class="seg seg--icons" role="group" aria-label="Show on the model">
        <button type="button" :aria-pressed="opts.figure" data-tog="figure" aria-label="Person for scale, 1.75 m" title="Person for scale (1.75 m)" @click="toggleFigure"><Icon name="person" /><span>Person</span></button>
        <button type="button" :aria-pressed="opts.dims" data-tog="dims" aria-label="Dimensions" title="Dimensions" @click="toggleDims"><Icon name="ruler" /><span>Sizes</span></button>
        <button type="button" :aria-pressed="props" data-tog="books" aria-label="Books and objects, appearance only" title="Books and objects (appearance only)" :disabled="!!snap.viewing" @click="toggleBooks"><Icon name="books" /><span>Books</span></button>
      </div>
    </div>

    <div v-if="viewBar" id="viewBar" ref="barEl" class="view-bar">
      <Icon name="eye" />
      <p><strong>{{ viewBar.title }}</strong><span>{{ viewBar.sub }}</span></p>
      <button type="button" class="btn btn--sm" :class="viewBar.kind === 'inspect' ? 'btn--quiet' : 'btn--ink'" data-act="end-view" @click="endView">{{ viewBar.kind === 'inspect' ? 'Back to the assembled model' : 'Back to latest' }}</button>
    </div>

    <div v-if="selPart" id="partCard" class="part-card">
      <div class="pc-head">
        <span class="pc-id mono">{{ selPart.id }}</span><strong>{{ selPart.label }}</strong>
        <button type="button" class="icon-btn icon-btn--sm" data-act="close-part" aria-label="Clear selection" @click="select(null)"><Icon name="close" /></button>
      </div>
      <dl class="pc-dl">
        <div><dt>Finished</dt><dd class="mono">{{ fmtMm(selPart.finished.l) }} × {{ fmtMm(selPart.finished.w) }} × {{ fmtMm(selPart.finished.t) }}</dd></div>
        <div><dt>Grain</dt><dd><span aria-hidden="true">{{ grainArrow(selPart) }}</span> {{ grainText(selPart) }}, along {{ fmtMm(selPart.finished.l) }}</dd></div>
        <div v-if="selPart.datum != null"><dt>Underside</dt><dd class="mono">{{ datumText(selPart.datum) }} from the floor</dd></div>
      </dl>
      <div v-if="mode !== 'pack'" class="pc-acts"><button type="button" class="btn btn--sm btn--quiet" data-go="pack" @click="go('pack')">In the cut list</button></div>
    </div>

    <div ref="footEl" class="stage-foot">
      <span v-if="materialNote" class="stage-note" data-note="check-material">{{ materialNote }}</span><span v-else />
      <span id="editMs" class="edit-ms" aria-hidden="true">{{ editMs }}</span>
    </div>
  </section>
</template>
